import { useCallback, useEffect, useState } from 'react';

// Photo search for the photos page (/api/site/photo-search). One box: a
// 1-3 digit number is a race number (read from the photos by Immich's OCR),
// anything else is words in captions and event names, and both together
// narrow the search ("501 curborough"). Deliberately narrow, by agreement:
// no "describe it" AI search and no faces.
// Searches run when the visitor presses Search or Enter, never as they type.
// Clicking a result opens it larger, with arrows to step through.


function when(photo) {
  return photo.date ? new Date(`${photo.date}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
}

function Viewer({ photos, index, onClose, onMove }) {
  const photo = photos[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onMove(1);
      if (e.key === 'ArrowLeft') onMove(-1);
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose, onMove]);

  return (
    <div className="site-viewer" role="dialog" aria-modal="true" aria-label="Photo" onClick={onClose}>
      <div className="site-viewer-inner" onClick={e => e.stopPropagation()}>
        <button type="button" className="site-viewer-close" onClick={onClose} aria-label="Close">×</button>
        <img src={photo.preview} alt={photo.caption || photo.album || 'Photo'} />
        <div className="site-viewer-info">
          <span>
            {photo.caption && <strong>{photo.caption.split('. From "Pictures past"')[0]}</strong>}
            {photo.caption && ' · '}
            {photo.album}
            {/* Event albums already say the date ("Curborough · 25 July 2026"). */}
            {when(photo) && !(photo.year && String(photo.album).includes(String(photo.year))) && `${photo.album ? ' · ' : ''}${when(photo)}`}
          </span>
          {photo.albumUrl && <a href={photo.albumUrl} target="_blank" rel="noreferrer">Open the album</a>}
        </div>
        {photos.length > 1 && (
          <>
            <button type="button" className="site-viewer-nav site-viewer-prev" onClick={() => onMove(-1)} aria-label="Previous photo">‹</button>
            <button type="button" className="site-viewer-nav site-viewer-next" onClick={() => onMove(1)} aria-label="Next photo">›</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function PhotoSearch({ onActiveChange }) {
  const [q, setQ] = useState('');
  const [result, setResult] = useState(null);   // {q, numbers, words, total, photos} | {error}
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const active = result !== null;
  useEffect(() => { onActiveChange(active); }, [active, onActiveChange]);

  const search = async (e) => {
    e.preventDefault();
    const query = q.trim();
    if (!query) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/site/photo-search?q=${encodeURIComponent(query)}`);
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) setResult({ error: "That's a lot of searches. Please wait a minute and try again." });
      else if (!res.ok) setResult({ error: data.error || 'The search is unavailable just now.' });
      else setResult(data);
    } catch {
      setResult({ error: 'The search is unavailable just now.' });
    }
    setBusy(false);
  };

  const clear = () => { setResult(null); setQ(''); };
  const photos = result?.photos || [];
  const move = useCallback((step) => setOpen(i => (i + step + photos.length) % photos.length), [photos.length]);
  const close = useCallback(() => setOpen(null), []);

  return (
    <div className="site-photo-search">
      <form className="site-search-form" onSubmit={search} role="search">
        <input type="search" value={q} onChange={e => setQ(e.target.value)} maxLength={60}
          placeholder="Race number, rider, hill or year, e.g. 501 curborough"
          aria-label="Search the photos by race number, or words in captions and event names" />
        <button type="submit" className="site-btn site-btn--ink" disabled={busy || !q.trim()}>{busy ? 'Searching…' : 'Search'}</button>
      </form>
      <p className="site-search-help">
        A race number finds photos where it can be read on the bike (not every photo shows its number clearly).
        Words search captions and event names. Use both to narrow it down.
      </p>

      {result && (
        <section className="site-search-results" aria-live="polite">
          <div className="site-search-results-head">
            {result.error
              ? <span className="site-field-error">{result.error}</span>
              : <span>{result.total === 0 ? 'No photos found' : `${result.total} ${result.total === 1 ? 'photo' : 'photos'}`} for <strong>{result.q}</strong>{result.total > photos.length ? `, showing the newest ${photos.length}` : ''}</span>}
            <button type="button" className="site-link-button" onClick={clear}>Back to all albums</button>
          </div>
          {photos.length > 0 && (
            <div className="site-search-grid">
              {photos.map((p, i) => (
                <button key={p.id} type="button" className="site-search-photo" onClick={() => setOpen(i)}
                  aria-label={`${p.caption || p.album}, ${when(p)}`}>
                  <img src={p.thumbnail} alt="" loading="lazy" />
                  <span>{p.year}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {open !== null && photos[open] && <Viewer photos={photos} index={open} onClose={close} onMove={move} />}
    </div>
  );
}
